import { type FC } from "react";

import { AppShellSection, Group, NavLink, Stack, Text } from "@mantine/core";
import {
    AnalyticsIcon,
    CalendarIcon,
    FolderIcon,
    GlobeIcon,
    HomeIcon,
    MagicWandIcon,
    RocketIcon,
    Sizes,
    SnapCameraKitIcon,
    TrendingIcon,
    UploadIcon,
} from "@snapchat/snap-design-system-icons";
import { IconFileText } from "@tabler/icons-react";
import { useRecoilValue } from "recoil";

import apisIconSvg from "@app-assets/svg/apis-icon.svg";

import { useCamkitEnabled } from "../../apis/hooks/useCamkitEnabled";
import { AssetLibraryIcon } from "../../assets/components/AssetLibraryIcon";
import teamMembersSvg from "../../camkit/assets/common/team-members.svg";
import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { defaultOrganizationContainerSelector } from "../../core/recoil/organizations";
import { useApiManagementRoutes, useV3Routes } from "../../core/routes/hooks";
import { CofConfigId } from "../../gql-generated/types";
import { MonetizationIcon } from "../../monetization/components/MonetizationIcon";
import { RemoteAssetsIcon } from "../../remote-assets/components/RemoteAssetsIcon";
import { BUSINESS_MANAGER_URL, PRIVACY_POLICY_URL, ROLE_ACTIONS, TERMS_OF_SERVICE_URL } from "../../shared/constants";
import { useSelectedFeature, useSelectedOrganizationContainer } from "../../shared/hooks";
import { usePermittedBasedOnLensFolderRoleAction } from "../../shared/hooks/roles";
import { useOrganizationsV2 } from "../../shared/hooks/useOrganizationsV2";
import { Feature, HOME_FEATURES, LENS_SCHEDULER_FEATURES } from "../../shared/models";

import { NavigationIcon, externalLinkCss, sectionDividerCss, teamMembersIconCss } from "./LeftNavigation.styles";
import { navLink, navLinkLabel, section } from "./LeftNavigationV3.module.css";
import {
    Apis,
    Apps,
    AssetLibraryTitle,
    CameraKit,
    CommunityAudience,
    Global,
    Home,
    HostedLenses,
    Insights,
    InviteTeamMembers,
    LensFolders,
    LensScheduler,
    Lenses,
    Monetization,
    PrivacyPolicy,
    RemoteAssets,
    TermsOfService,
    WebLenses,
    Welcome,
} from "./messages.generated";
import { SubmissionQuotaQuickView } from "./submission-quota";

interface LeftNavigationV3Props {
    closeMenu: () => void;
}

export const LeftNavigationV3: FC<LeftNavigationV3Props> = ({ closeMenu }) => {
    const { goToApisList } = useApiManagementRoutes();
    const { goToCommunityAudience, goToWelcome, goToContainerFeature, goToLensFolders, goToCameraKit, goToHome } =
        useV3Routes();

    const { orgId, lensFolderId, container: selectedContainer } = useSelectedOrganizationContainer();
    const defaultOrganizationContainerV1 = useRecoilValue(defaultOrganizationContainerSelector);
    const { selectedFeature } = useSelectedFeature();

    const menuItemSelected =
        !selectedFeature || HOME_FEATURES.includes(selectedFeature) ? "lens-folders" : selectedFeature;

    const { isPermitted: isPermittedToViewInsights } = usePermittedBasedOnLensFolderRoleAction(
        lensFolderId ?? "",
        ROLE_ACTIONS.READ_LENS_INSIGHTS,
    );

    const { configs } = useFeatureGates();

    const newUXenabled = configs[CofConfigId.MyLensesUxImprovements].enabled;
    const assetsEnabled = configs[CofConfigId.MyLensesUiAssetLibrary].enabled;
    const portalUnificationEnabled = configs[CofConfigId.MyLensesPortalUnification].enabled;
    const isHostedLensesEnabled = configs[CofConfigId.MyLensesHostedLenses].enabled;
    const isRemoteAssetsEnabled = configs[CofConfigId.MyLensesRemoteAssets].enabled;
    const isWebLensEnabled = configs[CofConfigId.MyLensesWebLensPublication].enabled;
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;
    const isMonetizationHubEnabled = configs[CofConfigId.MyLensesMonetizationInsights].enabled;

    const { data: isCamkitEnabledOnAnyOrg } = useCamkitEnabled();
    const { defaultOrganizationContainer: defaultOrganizationContainerV2 } = useOrganizationsV2();

    const hasDefaultOrg = isMyLensesOrgsV2Enabled ? !!defaultOrganizationContainerV2 : !!defaultOrganizationContainerV1;

    const shouldShowWelcome = !hasDefaultOrg;
    const shouldShowGlobalCameraKit = !hasDefaultOrg && portalUnificationEnabled;
    const shouldShowWebApis = isCamkitEnabledOnAnyOrg || configs[CofConfigId.MyLensesUiWebApis].enabled;

    const hasFeatures = (
        c: typeof selectedContainer,
    ): c is Extract<typeof selectedContainer, { features: unknown }> => {
        return c !== undefined && "features" in c;
    };

    const showInsightsForContainers = isMyLensesOrgsV2Enabled
        ? true
        : hasFeatures(selectedContainer) && selectedContainer.features.has(Feature.insights);

    const shouldShowInsights =
        portalUnificationEnabled ||
        (lensFolderId ? showInsightsForContainers && isPermittedToViewInsights : showInsightsForContainers);

    const shouldShowMonetization = shouldShowInsights && isMonetizationHubEnabled;

    const shouldShowCommunityAudience = configs[CofConfigId.MyLensesUiCommunityAudience].enabled && hasDefaultOrg;

    return (
        <>
            {hasDefaultOrg && (
                <>
                    <AppShellSection className={section} data-testid="lenses-navigation-menu">
                        <Text size="sm" fw={500} c="gray" mb="xs">
                            <Lenses />
                        </Text>
                        <NavLink
                            onClick={() => {
                                goToHome(orgId);
                                closeMenu();
                            }}
                            active={selectedFeature === Feature.home}
                            leftSection={
                                <Group gap="sm">
                                    <HomeIcon size={Sizes.Large} />
                                </Group>
                            }
                            label={<Home />}
                            className={navLink}
                            data-active={selectedFeature === Feature.home}
                            classNames={{
                                label: navLinkLabel,
                            }}
                        />
                        {newUXenabled ? (
                            <NavLink
                                onClick={() => {
                                    goToLensFolders(orgId);
                                    closeMenu();
                                }}
                                active={menuItemSelected === Feature.lensFolders}
                                leftSection={
                                    <Group gap="sm">
                                        <FolderIcon size={Sizes.Large} />
                                    </Group>
                                }
                                label={<LensFolders />}
                                className={navLink}
                                data-active={menuItemSelected === Feature.lensFolders}
                                classNames={{
                                    label: navLinkLabel,
                                }}
                            />
                        ) : (
                            <NavLink
                                onClick={() => {
                                    goToContainerFeature(Feature.lenses, orgId ?? "", lensFolderId);
                                    closeMenu();
                                }}
                                active={menuItemSelected === Feature.lenses}
                                leftSection={
                                    <Group gap="sm">
                                        <MagicWandIcon size={Sizes.Large} />
                                    </Group>
                                }
                                label={<Lenses />}
                                className={navLink}
                                data-active={menuItemSelected === Feature.lenses}
                                classNames={{
                                    label: navLinkLabel,
                                }}
                            />
                        )}
                        {shouldShowInsights && (
                            <NavLink
                                onClick={() => {
                                    goToContainerFeature(Feature.insights, orgId ?? "", lensFolderId);
                                    closeMenu();
                                }}
                                active={menuItemSelected === Feature.insights}
                                leftSection={
                                    <Group gap="sm">
                                        <AnalyticsIcon size={Sizes.Large} />
                                    </Group>
                                }
                                label={<Insights />}
                                className={navLink}
                                data-active={menuItemSelected === Feature.insights}
                                classNames={{
                                    label: navLinkLabel,
                                }}
                            />
                        )}
                        {shouldShowMonetization && (
                            <NavLink
                                onClick={() => {
                                    goToContainerFeature(Feature.monetization, orgId ?? "", lensFolderId);
                                    closeMenu();
                                }}
                                active={menuItemSelected === Feature.monetization}
                                leftSection={
                                    <Group gap="sm">
                                        <MonetizationIcon size={Sizes.Large} />
                                    </Group>
                                }
                                label={<Monetization />}
                                className={navLink}
                                data-active={menuItemSelected === Feature.monetization}
                                classNames={{
                                    label: navLinkLabel,
                                }}
                            />
                        )}
                        {assetsEnabled && (
                            <NavLink
                                onClick={() => {
                                    goToContainerFeature(Feature.assets, orgId ?? "");
                                    closeMenu();
                                }}
                                active={menuItemSelected === Feature.assets}
                                leftSection={
                                    <Group gap="sm">
                                        <AssetLibraryIcon size={Sizes.Large} />
                                    </Group>
                                }
                                label={<AssetLibraryTitle />}
                                className={navLink}
                                data-active={menuItemSelected === Feature.assets}
                                classNames={{
                                    label: navLinkLabel,
                                }}
                            />
                        )}
                        {isHostedLensesEnabled && (
                            <NavLink
                                onClick={() => {
                                    goToContainerFeature(Feature.hostedLenses, orgId ?? "");
                                    closeMenu();
                                }}
                                active={menuItemSelected === Feature.hostedLenses}
                                leftSection={
                                    <Group gap="sm">
                                        <GlobeIcon size={Sizes.Large} />
                                    </Group>
                                }
                                label={<HostedLenses />}
                                className={navLink}
                                data-active={menuItemSelected === Feature.hostedLenses}
                                classNames={{
                                    label: navLinkLabel,
                                }}
                            />
                        )}
                        {isRemoteAssetsEnabled && (
                            <NavLink
                                onClick={() => {
                                    goToContainerFeature(Feature.remoteAssets, orgId ?? "");
                                    closeMenu();
                                }}
                                active={menuItemSelected === Feature.remoteAssets}
                                leftSection={
                                    <Group gap="sm">
                                        <RemoteAssetsIcon size={Sizes.Large} />
                                    </Group>
                                }
                                label={<RemoteAssets />}
                                className={navLink}
                                data-active={menuItemSelected === Feature.remoteAssets}
                                classNames={{
                                    label: navLinkLabel,
                                }}
                            />
                        )}
                        {isWebLensEnabled && (
                            <NavLink
                                onClick={() => {
                                    goToContainerFeature(Feature.webLenses, orgId ?? "");
                                    closeMenu();
                                }}
                                active={selectedFeature === Feature.webLenses}
                                leftSection={
                                    <Group gap="sm">
                                        <UploadIcon size={Sizes.Large} />
                                    </Group>
                                }
                                label={<WebLenses />}
                                className={navLink}
                                data-active={selectedFeature === Feature.webLenses}
                                classNames={{
                                    label: navLinkLabel,
                                }}
                            />
                        )}
                    </AppShellSection>

                    {portalUnificationEnabled && (
                        <AppShellSection className={section} data-testid="camera-kit-navigation-menu">
                            <Text size="sm" fw={500} c="dimmed" mb="xs" className={sectionDividerCss}>
                                <CameraKit />
                            </Text>
                            <NavLink
                                onClick={() => {
                                    goToContainerFeature(Feature.lensScheduler, orgId ?? "");
                                    closeMenu();
                                }}
                                active={
                                    selectedFeature && LENS_SCHEDULER_FEATURES.includes(selectedFeature)
                                        ? true
                                        : selectedFeature === Feature.lensScheduler
                                }
                                leftSection={
                                    <Group gap="sm">
                                        <CalendarIcon size={Sizes.Large} />
                                    </Group>
                                }
                                label={<LensScheduler />}
                                className={navLink}
                                data-active={
                                    selectedFeature && LENS_SCHEDULER_FEATURES.includes(selectedFeature)
                                        ? true
                                        : selectedFeature === Feature.lensScheduler
                                }
                                classNames={{
                                    label: navLinkLabel,
                                }}
                            />
                            <NavLink
                                onClick={() => {
                                    goToContainerFeature(Feature.apps, orgId ?? "");
                                    closeMenu();
                                }}
                                active={selectedFeature === Feature.apps}
                                leftSection={
                                    <Group gap="sm">
                                        <RocketIcon size={Sizes.Large} />
                                    </Group>
                                }
                                label={<Apps />}
                                className={navLink}
                                data-active={selectedFeature === Feature.apps}
                                classNames={{
                                    label: navLinkLabel,
                                }}
                            />
                        </AppShellSection>
                    )}
                </>
            )}
            <AppShellSection className={section} data-testid="global-navigation-menu" grow>
                <Text size="sm" fw={500} c="dimmed" mb="xs" className={hasDefaultOrg ? sectionDividerCss : ""}>
                    <Global />
                </Text>
                {shouldShowWelcome && (
                    <NavLink
                        onClick={() => {
                            goToWelcome();
                            closeMenu();
                        }}
                        active={selectedFeature === Feature.welcome}
                        leftSection={
                            <Group gap="sm">
                                <MagicWandIcon size={Sizes.Large} />
                            </Group>
                        }
                        label={<Welcome />}
                        className={navLink}
                        data-active={selectedFeature === Feature.welcome}
                        classNames={{
                            label: navLinkLabel,
                        }}
                    />
                )}
                {shouldShowGlobalCameraKit && (
                    <NavLink
                        onClick={() => {
                            goToCameraKit();
                            closeMenu();
                        }}
                        active={selectedFeature === Feature.cameraKit}
                        leftSection={
                            <Group gap="sm">
                                <SnapCameraKitIcon size={Sizes.Large} />
                            </Group>
                        }
                        label={<CameraKit />}
                        className={navLink}
                        data-active={selectedFeature === Feature.cameraKit}
                        classNames={{
                            label: navLinkLabel,
                        }}
                    />
                )}

                {shouldShowWebApis && (
                    <NavLink
                        onClick={() => {
                            goToApisList();
                            closeMenu();
                        }}
                        active={selectedFeature === Feature.apis}
                        leftSection={
                            <Group gap="sm">
                                <NavigationIcon src={apisIconSvg} alt="APIs icon." />
                            </Group>
                        }
                        label={<Apis />}
                        className={navLink}
                        data-active={selectedFeature === Feature.apis}
                        classNames={{
                            label: navLinkLabel,
                        }}
                    />
                )}

                {shouldShowCommunityAudience && (
                    <NavLink
                        onClick={() => {
                            goToCommunityAudience();
                            closeMenu();
                        }}
                        active={selectedFeature === Feature.communityAudience}
                        leftSection={
                            <Group gap="sm">
                                <TrendingIcon size={Sizes.Large} />
                            </Group>
                        }
                        label={<CommunityAudience />}
                        className={navLink}
                        data-active={selectedFeature === Feature.communityAudience}
                        classNames={{
                            label: navLinkLabel,
                        }}
                    />
                )}
            </AppShellSection>
            <AppShellSection className={section} pb="xl">
                <Stack>
                    <SubmissionQuotaQuickView />
                    <Group>
                        <img src={teamMembersSvg} alt="team members" className={teamMembersIconCss} />
                        <a
                            className={externalLinkCss}
                            href={`${BUSINESS_MANAGER_URL}/${orgId}/settings/members`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <InviteTeamMembers />
                        </a>
                    </Group>
                    <Group>
                        <IconFileText size={Sizes.Medium} />
                        <a className={externalLinkCss} href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
                            <PrivacyPolicy />
                        </a>
                    </Group>
                    <Group>
                        <IconFileText size={Sizes.Medium} />
                        <a className={externalLinkCss} href={TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer">
                            <TermsOfService />
                        </a>
                    </Group>
                </Stack>
            </AppShellSection>
        </>
    );
};
